import {convertImageToWebP, getCountryCode} from '@/helpers/shadcn/utils'
import {useAzureBlob} from '@/hooks/useAzureBlob-hook'
import {useImagesUpload} from '@/hooks/useImagesUpload'
import {validateProductIds} from '@/services/ProductsService/product'
import {getSastoken, updateProductMediaMetadata} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {FILE} from '@/utils/constants/commonConstants'
import {PRODUCT_TYPE} from '@/utils/constants/globalConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useState} from 'react'

const ALLOWED_IMAGE_TYPES = new Set(FILE.ALLOWED_IMAGE_TYPES)
const MAX_CONCURRENT_UPLOADS = 5

const formatFileSize = bytes => {
  if (!bytes) return undefined
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

// FileUploadV2's single-image preview reports delete via a -1 sentinel ("remove the only
// file") instead of a real index, since it only ever shows one file at a time.
const removeAt = (list, index) => (index === -1 ? list.slice(1) : list.filter((_, i) => i !== index))

/**
 * Extracted logic for the v2 UploadMediaModal: file selection/validation, image type
 * selection and the product/other-entity upload flows.
 */
export const useUploadMediaModal = ({setOpenUpload, initialEntityType}) => {
  const {uploading, setUploadErrors, onUploadImages, uploadErrors, progress, setIsSingleImgUpload, setAllProdIds} =
    useImagesUpload()
  const {uploadMediaFilesOnBlob} = useAzureBlob()
  const dispatch = useAppDispatch()

  const [images, setImages] = useState([])
  const [convertedImgs, setConvertedImages] = useState([])
  const [entityType, setEntityType] = useState(initialEntityType ?? PRODUCT_TYPE.PRODUCT)
  const isProductEntity = entityType === PRODUCT_TYPE.PRODUCT

  const {
    imageCountAllowed = 50,
    maxCollectionOfImageSize = '25MB',
    maxIndividualImageSize = '2MB',
  } = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}

  const maxImageCollectionSize = isProductEntity ? maxCollectionOfImageSize : '50MB'
  const maxSizeInMB = Number(maxImageCollectionSize?.match(/\d+/)?.[0])
  const maxAllowedImageCount = isProductEntity ? imageCountAllowed : 50

  // The parent remounts this component (via a `key` tied to openUpload) on every open/close,
  // so all local/hook state resets for free - no manual reset needed here.
  const isCloseModal = () => {
    setOpenUpload(false)
  }

  const individualImageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const fileNames = []
    let hasError = false
    const maxSizeBytes = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES

    for (const file of updatedArray) {
      file.error = false

      if (file?.size > maxSizeBytes) {
        file.error = true
        fileNames.push(file.name)
        hasError = true
      }
    }

    if (hasError) {
      // const errorConst = fileNames.length > 1 ? 'these files are' : 'this file is'
      // const errMsg =
      //   updatedArray.length === 1
      //     ? `Each image must be less than or equal to ${maxIndividualImageSize}.`
      //     : `${fileNames.join(', ')} ${errorConst} exceeding the ${maxIndividualImageSize} individual file size limit.`

      // setUploadErrors([errMsg])
      return false
    }

    return true
  }

  const imageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const maxAllowedSize = maxSizeInMB * FILE.SIZE_BYTES
    const totalSize = updatedArray.reduce((sum, file) => sum + (file?.size || 0), 0)

    if (updatedArray.length > maxAllowedImageCount) {
      setUploadErrors([`You can not upload more than ${maxAllowedImageCount} files. Remove some files to continue.`])
      return false
    }

    if (totalSize >= maxAllowedSize) {
      setUploadErrors([`Total image size must not exceed ${maxSizeInMB} MB.`])
      return false
    }

    return true
  }

  // The v2 FileUpload calls onUpdate once per newly selected file.
  const handleFileUpdate = async file => {
    if (!file || !ALLOWED_IMAGE_TYPES.has(file.type)) {
      setUploadErrors(['Unsupported file format. Please upload jpg, png or webp images.'])
      return
    }

    if (images.some(existing => existing.name === file.name)) {
      return
    }

    const updatedImages = [...images, file]
    imageValidation(updatedImages)
    individualImageValidation(updatedImages)

    const converted = await convertImageToWebP(file, {entityType})
    setImages(prev => [...prev, converted.file])
    setConvertedImages(prev => [...prev, {metadata: converted.metadata, webpFile: converted.webpFile}])
  }

  const handleFileDelete = index => {
    setImages(prev => {
      const updated = removeAt(prev, index)

      if (individualImageValidation(updated) && imageValidation(updated)) {
        setUploadErrors([])
      }

      return updated
    })

    setConvertedImages(prev => removeAt(prev, index))
  }

  const handleFileError = error => {
    setUploadErrors([error])
  }

  const filterValidImgs = async imgs => {
    let validProductIds
    try {
      validProductIds = await validateProductIds(imgs.map(item => item?.metadata?.id))
    } catch (error) {
      console.error('Failed to validate product IDs:', error)
      setUploadErrors(['Unable to validate product IDs. Please try again.'])
      throw error
    }

    const validImgs = imgs.filter(item => validProductIds?.[item?.metadata?.id] !== false)
    const invalidImgs = imgs.filter(item => validProductIds?.[item?.metadata?.id] === false)
    return {validImgs, invalidImgs}
  }

  const fetchSasTokens = validImgs =>
    getSastoken({
      media: validImgs.map(entry => ({
        fileName: entry.webpFile.name,
        mediaType: 'IMAGE',
        displayOrder: entry.metadata.displayOrder,
        fileSize: entry.webpFile.size,
      })),
      countryCode: getCountryCode(),
    })

  const uploadFilesToBlob = (sasMediaItems, validImgs) =>
    Promise.all(
      sasMediaItems.map(async ({sasToken, directory, fileName}, index) => {
        const originalFile = validImgs[index].webpFile
        const file = new File([originalFile], `${fileName}`, {type: originalFile.type})
        const results = await uploadMediaFilesOnBlob({sasToken, directory}, [file])
        return results?.[0] ?? {fileName, success: false, error: new Error('Upload returned no result')}
      })
    )

  const saveMediaMetadata = async (validImgs, sasMediaItems) => {
    try {
      return await Promise.all(
        Object.entries(
          validImgs.reduce((acc, entry, index) => {
            const id = entry.metadata.id
            const sasItem = sasMediaItems[index] ?? null
            const uploadedFileName = sasItem?.fileName
            const mediaUrl = sasItem?.cdnUrl

            if (!acc[id]) acc[id] = []
            acc[id].push({
              fileName: uploadedFileName,
              mediaType: 'IMAGE',
              displayOrder: entry?.metadata?.displayOrder,
              mediaUrl,
            })
            return acc
          }, {})
        ).map(([productId, media]) => updateProductMediaMetadata({productId, media, countryCode: getCountryCode()}))
      )
    } catch (error) {
      console.error('API failed =====', error)
      setUploadErrors([error])
      throw error
    }
  }

  const uploadOne = ({webpFile, metadata}) => {
    const formData = new FormData()
    formData.append('images', webpFile)
    const modifiedMetadata = {...metadata, referenceEntity: entityType}
    formData.append('metadata', JSON.stringify({mediaUploadMetadata: [modifiedMetadata]}))
    return onUploadImages(formData)
  }

  const uploadWithConcurrencyLimit = async items => {
    const results = new Array(items.length)
    const queue = items.map((item, index) => ({item, index}))

    const worker = async () => {
      while (queue.length > 0) {
        const next = queue.shift()
        if (!next) return
        try {
          results[next.index] = {status: 'fulfilled', value: await uploadOne(next.item)}
        } catch (reason) {
          results[next.index] = {status: 'rejected', reason}
        }
      }
    }

    await Promise.all(Array.from({length: Math.min(MAX_CONCURRENT_UPLOADS, items.length)}, () => worker()))
    return results
  }

  const handleUpload = async () => {
    setUploadErrors([])
    setAllProdIds([])
    setIsSingleImgUpload(false)

    if (!convertedImgs?.length) {
      return
    }

    if (isProductEntity) {
      try {
        const {validImgs, invalidImgs} = await filterValidImgs(convertedImgs)

        if (invalidImgs.length > 0) {
          const invalidIds = [...new Set(invalidImgs.map(e => e.metadata.id))].join(', ')
          setUploadErrors([`The following product IDs were not found and skipped: ${invalidIds}`])
        }

        if (!validImgs.length) {
          dispatch(
            setToastNotifications({show: true, message: 'No valid product IDs found. Upload cancelled.', type: 'error'})
          )
          return
        }

        const sasTokenResponse = await fetchSasTokens(validImgs)
        const sasMediaItems = sasTokenResponse?.media ?? sasTokenResponse?.data?.media ?? []

        const uploadResults = await uploadFilesToBlob(sasMediaItems, validImgs)
        if (uploadResults.filter(r => !r.success).length > 0) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Some images failed to upload. Please review the errors and retry.',
              type: 'warning',
            })
          )
          return
        }

        await saveMediaMetadata(validImgs, sasMediaItems)
        dispatch(setToastNotifications({show: true, message: 'Images uploaded successfully', type: 'success'}))
        setImages([])

        if (invalidImgs.length > 0) {
          return
        }

        isCloseModal()
      } catch (error) {
        console.error('Unexpected error during uploads:', error)
        dispatch(
          setToastNotifications({show: true, message: `Upload failed: ${error?.message || error}`, type: 'error'})
        )
      }
    } else {
      try {
        const results = await uploadWithConcurrencyLimit(convertedImgs)
        if (results.filter(r => r.status === 'rejected').length === 0) {
          isCloseModal()
          dispatch(setToastNotifications({show: true, message: 'Images uploaded successfully', type: 'success'}))
        } else {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Some images failed to upload. Please review the errors and retry.',
              type: 'warning',
            })
          )
        }
      } catch (error) {
        console.error('Unexpected error during uploads:', error)
      }
    }
  }

  const files = images.map(file => ({
    name: file.name,
    size: formatFileSize(file.size),
    hasError: !!file.error,
    errorMessage: file.error ? `Exceeds ${maxIndividualImageSize} limit` : undefined,
    status: uploading ? 'uploading' : 'completed',
    progress: uploading ? progress : undefined,
  }))

  const getHasErrors = () => uploadErrors?.length > 0 || files.find(file => file.hasError)
  const imageUri = images.length === 1 && !getHasErrors() ? URL?.createObjectURL(images[0]) : ''
  const getUploadErrors = () =>
    uploadErrors?.length > 0 ? uploadErrors : files.length === 1 && files[0].hasError ? [files[0].errorMessage] : []

  return {
    entityType,
    setEntityType,
    files,
    hasErrors: getHasErrors(),
    uploadErrors: getUploadErrors(),
    uploading,
    imageUri,
    maxImageCollectionSize,
    maxAllowedImageCount,
    maxIndividualImageSize,
    handleFileUpdate,
    handleFileDelete,
    handleFileError,
    handleUpload,
    isCloseModal,
  }
}
